/**
 * `/users/:id` — one account: its details, what it belongs to, and the three
 * administrative acts.
 *
 * The roles listed here are the ones assigned **directly**. A role reaching this
 * user through a group is not shown, deliberately: it cannot be taken away from
 * them here, because it was never given to them, and listing it would produce a
 * remove button that either lies or removes it from everybody in the group.
 */
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupsIcon from '@mui/icons-material/Groups';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AsyncContent } from '../../components/AsyncContent';
import { Columns } from '../../components/Columns';
import { Details } from '../../components/Details';
import { EntityDetail } from '../../components/EntityDetail';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../auth/useAuth';
import { Slot } from '../../modules/Slot';
import { useUserActions } from '../entities/userActions';
import { getUser, getUserMemberships } from '../../api/identity';


export default function UserDetailPage() {
  const { t } = useTranslation('people');
  const { id } = useParams();
  const userId = Number(id);
  const { user: self } = useAuth();

  const user = useAsync(() => getUser(userId), [userId]);
  // No `enabled` guard: the whole /users route already sits behind user.read,
  // so a caller who could not read this would never have reached the screen.
  // A disabled useAsync under AsyncContent renders skeletons that never fill in.
  const memberships = useAsync(() => getUserMemberships(userId), [userId]);

  // The same set `UsersPage`'s rows offer, declared once in
  // `../entities/userActions`. `EntityDetail` renders the buttons and mounts the
  // dialogs; nothing about any of them is written on this screen.
  const actions = useUserActions({
    onChanged: (record, key) => {
      if (record) user.setData({ user: record });
      // A password change alters no field of the account, so there is nothing to
      // re-render — only a fact to report.
      if (key === 'password') setPasswordSet(true);
      if (key === 'edit' || key === 'password') memberships.reload();
    },
  });

  const [passwordSet, setPasswordSet] = useState(false);

  return (
    <AsyncContent state={user}>
      {({ user: record }) => (
        <EntityDetail
          backTo="/users"
          backLabel={t('userDetail.back')}
          title={record.displayName}
          badges={
            <>
              {!record.isActive && <Chip size="small" label={t('users.deactivated')} />}
              {record.mustChangePassword && (
                <Chip size="small" color="warning" label={t('userDetail.mustChangePassword')} />
              )}
              {self?.id === record.id && (
                <Chip size="small" color="info" variant="outlined" label={t('userDetail.you')} />
              )}
            </>
          }
          description={record.email}
          entity={actions.forRow(record)}
          // Modules' buttons for this person — messaging's "Send message".
          actions={<Slot name="user.detail.actions" props={{ user: record }} />}
          error={actions.error}
          notices={
            passwordSet && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSet(false)}>
                {t('userDetail.passwordChanged')}
              </Alert>
            )
          }
        >
          <Columns>
            <Panel title={t('userDetail.account')}>
              <Details
                rows={[
                  [t('userDetail.field.email'), record.email],
                  [t('userDetail.field.displayName'), record.displayName],
                  [t('userDetail.field.status'), record.isActive ? t('users.active') : t('users.deactivated')],
                  [t('userDetail.field.lastSignIn'), <Timestamp key="last" value={record.lastLoginAt} />],
                  [t('userDetail.field.created'), <Timestamp key="created" value={record.createdAt} absolute />],
                ]}
              />
            </Panel>

            <Panel title={t('userDetail.memberships')}>

              <AsyncContent state={memberships} placeholder="spinner">
                {({ groups, roles }) => (
                  <Stack spacing={2.5}>
                    <Box>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                        <GroupsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('userDetail.groups')}
                        </Typography>
                      </Stack>
                      {groups.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('userDetail.noGroups')}
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          {groups.map((group) => (
                            <Chip
                              key={group.id}
                              size="small"
                              label={group.name}
                              component={RouterLink}
                              to={`/groups/${group.id}`}
                              clickable
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Divider />

                    <Box>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                        <BadgeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('userDetail.directRoles')}
                        </Typography>
                      </Stack>
                      {roles.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('userDetail.noDirectRoles')}
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          {roles.map((role) => (
                            <Chip
                              key={role.id}
                              size="small"
                              label={role.name}
                              component={RouterLink}
                              to={`/roles/${role.id}`}
                              clickable
                            />
                          ))}
                        </Stack>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {t('userDetail.indirectRolesNote')}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </AsyncContent>
            </Panel>

            {/* Modules' panels for this person — rocketchat's username. They
                flow on in the same two columns; none adds no cell. */}
            <Slot name="user.detail.sections" props={{ user: record }} />
          </Columns>

        </EntityDetail>
      )}
    </AsyncContent>
  );
}
